// Routing primitives: the SPA uses pushState everywhere except the demo
// iframe at /demo.html, which still uses hash routing because its pathname
// is fixed. navigate() dispatches a synthetic popstate so back/forward and
// programmatic navigation share one code path in app.js.
import { isLoadingButton, hrefToAppPath } from "./dom.js";
import { getScheduleIdFromPath } from "./schedule-format.js";
import { getQuoteIdFromPath } from "./quote-format.js";

// The sandboxed demo iframe is served at /demo.html and cannot rely on
// pushState (its pathname is always /demo.html). Keep hash-based routing
// as a local fallback ONLY on that page; everything else uses pushState.
export function isDemoPage() {
  return typeof window !== "undefined" && window.location.pathname === "/demo.html";
}

export function route() {
  if (isDemoPage()) {
    return window.location.hash.replace(/^#/, "") || "/";
  }
  // On the main app, route() reflects the real URL pathname (+ query), so
  // direct links like /company/payments survive a hard refresh.
  const path = window.location.pathname || "/";
  const query = window.location.search || "";
  return path + query;
}

export function parseRouteState() {
  const [path = "/", query = ""] = String(route() || "/").split("?");
  return {
    path: path || "/",
    query: new URLSearchParams(query)
  };
}

export function navigate(path) {
  if (route() === path) return;
  const activeButton = document.activeElement;
  if (isLoadingButton(activeButton)) {
    activeButton.classList.add("is-loading");
    activeButton.setAttribute("aria-busy", "true");
    activeButton.disabled = true;
  }
  if (isDemoPage()) {
    window.location.hash = path;
    return;
  }
  // pushState keeps the real URL in sync; dispatching popstate runs the
  // same render path as back/forward navigation so there's one code path.
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function getInvoiceIdFromPath(path) {
  const match = String(path || "").match(/^\/invoices\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /pay/:token — the DEMO's client-facing pay page. Only the demo routes here
// (app.js gates the dispatch on isDemoPage()): in production this surface is
// server-rendered by the API at /pay/:token, never by the SPA. Not in
// routeRequiresAuth — it is the payer's page, not the user's.
export function getPayTokenFromPath(path) {
  const match = String(path || "").match(/^\/pay\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /q/:token — the DEMO's client-facing quote page (accept / decline). Only
// the demo routes here, like /pay/:token above: in production this page is
// server-rendered by the API. Not in routeRequiresAuth — it's the client's
// page, not the user's.
export function getQuoteTokenFromPath(path) {
  const match = String(path || "").match(/^\/q\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /invite/:token — deliberately NOT added to routeRequiresAuth (below): the
// invite-accept page must stay reachable while logged out (spec §6.3). It's
// the first SPA route with a dynamic path segment that's public rather than
// auth-gated, so app.js's isPublicAuthRoute check has to name it explicitly.
export function getInviteTokenFromPath(path) {
  const match = String(path || "").match(/^\/invite\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function wireHashButtonLoading() {
  document.addEventListener("click", (evt) => {
    const link = evt.target instanceof Element ? evt.target.closest("a.ar-btn") : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const path = hrefToAppPath(link.getAttribute("href"));
    if (!path || route() === path) return;
    link.classList.add("is-loading");
    link.setAttribute("aria-busy", "true");
  });
}

// Intercept clicks on in-app <a href="/..."> links so browsers don't trigger
// full page reloads. Modifier-clicks (cmd/ctrl/shift/alt) and target=_blank
// still open in a new tab as normal.
export function wireAppLinkInterception() {
  document.addEventListener("click", (evt) => {
    if (evt.defaultPrevented) return;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    if (typeof evt.button === "number" && evt.button !== 0) return;
    const link = evt.target instanceof Element ? evt.target.closest("a") : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== "" && link.target !== "_self") return;
    if (link.hasAttribute("download")) return;
    if (link.dataset.noIntercept === "true") return;
    const href = link.getAttribute("href");
    const path = hrefToAppPath(href);
    if (!path) return;
    // /features/* and /for/* are static marketing pages served as real files,
    // and *.html are standalone pages (demos, etc.) — none are SPA routes. Let
    // the browser do a full navigation so they actually load, instead of
    // pushState-routing into a screen that doesn't exist. (See plan rule 4:
    // these paths must not collide with SPA routes.)
    if (path.endsWith(".html") || /^\/(features|for)(\/|$)/.test(path)) return;
    // On the demo page, leave #/ links to the default hashchange behaviour —
    // the browser just changes the hash, which the app already listens for.
    if (isDemoPage() && String(href || "").startsWith("#/")) return;
    evt.preventDefault();
    navigate(path);
  });
}

export function routeRequiresAuth(path) {
  const protectedRoutes = new Set([
    "/company",
    "/invoices",
    "/invoices/new",
    "/quotes",
    "/quotes/new",
    "/schedules",
    "/schedules/new",
    "/contacts",
    "/contacts/add",
    "/team",
    "/profile"
  ]);
  if (protectedRoutes.has(path)) return true;
  // Sub-routes under /company and /profile (tabs) are also protected
  if (/^\/company(\/|$)/.test(path)) return true;
  if (/^\/profile(\/|$)/.test(path)) return true;
  return Boolean(getInvoiceIdFromPath(path)) || Boolean(getQuoteIdFromPath(path)) || Boolean(getScheduleIdFromPath(path)) || path.startsWith("/schedules/");
}

// Valid section ids for /company/<section> and /profile/<tab> sub-routes.
// "payments" merges the former "banking-details" + "online-payments" sections;
// company.js redirects those legacy paths to /company/payments.
export const COMPANY_TABS = ["company-details", "payments", "invoicing", "reminders"];
export const PROFILE_TABS = ["personal-info", "security", "billing"];

export function getCompanyTabFromPath(path) {
  const match = String(path || "").match(/^\/company\/([^/]+)$/);
  if (!match) return null;
  return COMPANY_TABS.includes(match[1]) ? match[1] : null;
}

export function getProfileTabFromPath(path) {
  const match = String(path || "").match(/^\/profile\/([^/]+)$/);
  if (!match) return null;
  return PROFILE_TABS.includes(match[1]) ? match[1] : null;
}
